import React, { Activity, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import {
  FolderManagerActionTypes,
  FolderManagerItemActionNames,
  FolderManagerItemActionTypes,
  FolderManagerMimeTypes,
} from '../../../../../interfaces';
import { CircularLoader } from '../../../../../shared';
import { FolderManagerGridItem } from '../../FolderManagerGridItem';
import { FolderManagerItemActions } from '../../FolderManagerItemActions';
import { FileDropContainer } from '../../FileDropContainer';
import { FolderIcon, DeckIcon } from '../../../../../components/UI/NgIcons';
import { viewStateSelectors } from '../../../../../store';
import { skipToken } from '@reduxjs/toolkit/query';
import { SlidePreviewDialog } from '../../../../../pages/Presentations/components/SlidePreviewDialog';
import { ImagePreviewDialog } from '@clatter/ui';
import { SortableContext } from '@dnd-kit/sortable';
import { useDnDItem } from '../../../../../hooks';
import { useGetSubFoldersQuery, useGetFolderAssetsQuery, useGetAssetThumbnailUrlQuery } from '../../../../../services';
import { markAlreadyAddedSlides } from '../../../../../helpers';
import {
  DEFAULT_SLIDES_FILTERS,
  filterAndSortSlides,
  GROUP_BY_OPTIONS,
  SlidesViewFilters,
  filterAndSortDecks,
} from '../../FolderManagerSlidesViewFilters';
import { useUploadNewAsset } from '../../../UploadNewAssetContext';

//region INTERFACES
export type FolderItemAction = { name: FolderManagerItemActionNames; type: FolderManagerItemActionTypes };
export type FolderActionsConfig = Partial<Record<FolderManagerMimeTypes, FolderItemAction[]>>;

export interface FolderRowProps {
  item: FolderManagerItem;
  depth: number;
  selectedRows: FolderManagerItem[];
  acceptedDragTypes?: string[];
  viewStateKey?: string;
  selectedKeys?: Set<string>;
  overId?: string | number | null;
  enableDnD?: boolean;
  disableUpload?: boolean;
  parentFolderId?: number;
  isParentSlideRepositoryFolder?: boolean;
  disableSelectAll?: boolean;
  compact?: boolean;
  maxGridCols?: number;
  addButtonTooltip?: string;
  filters?: SlidesViewFilters;
  addActionName?: FolderManagerItemActionNames;
  actionsConfig?: FolderActionsConfig;
  disabledIds?: Set<number>;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onAction: (data: any) => void;
  onSelect: (data: any) => void;
  onDoubleClick: (data: any) => void;
}
//endregion

//region HELPERS
export const isFolder = (item: FolderManagerItem) =>
  item.type === FolderManagerMimeTypes.FOLDER || item.type === FolderManagerMimeTypes.WORKSPACE;

export const isDeck = (item: FolderManagerItem, isParentSlideRepositoryFolder?: boolean) =>
  item.type === FolderManagerMimeTypes.PPTX && !!isParentSlideRepositoryFolder;

export const isAsset = (item: FolderManagerItem, isParentSlideRepositoryFolder?: boolean) =>
  !isFolder(item) && !isDeck(item, isParentSlideRepositoryFolder);

//endregion

//region STYLED
const StyledFolderRowWrapper = styled.div<{ $compact?: boolean }>`
  margin-bottom: ${({ $compact, theme }) => ($compact ? theme.spacing.xSmall : theme.spacing.default)};
`;

const StyledRowHeader = styled.div<{ $isDeck?: boolean; $isOver?: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  background-color: ${({ $isDeck, theme }) => ($isDeck ? theme.palette.white : '#F9FAFB')};

  border-radius: 7px;
  border: 1px solid ${({ theme }) => theme.palette.cardBorder};

  border-left: ${({ $isDeck, theme }) =>
    $isDeck ? `3px solid ${theme.palette.itemTypeColors.slide}` : `3px solid $${theme.palette.cardBorder}`};
  user-select: none;
  margin-bottom: 4px;
  outline: ${({ $isOver, theme }) => ($isOver ? `2px solid ${theme.palette.primaryColor}` : 'none')};
  outline-offset: -2px;

  &:hover {
    background-color: ${({ theme }) => theme.palette.cardBorder};
  }

  .chevron {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 10px;
    font-size: 10px;
    color: ${({ theme }) => theme.palette.text};
    flex-shrink: 0;
  }

  .row-icon {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    color: ${({ $isDeck, theme }) => ($isDeck ? theme.palette.primaryButton : theme.palette.textDimmed)};

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .row-name {
    flex: 1;
    font-weight: 600;
    font-size: ${({ theme }) => theme.fontSize.body};
    color: ${({ theme }) => theme.palette.text};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .row-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
`;

const StyledCountBadge = styled.span<{ $isDeck: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.palette.white};
  background-color: ${({ $isDeck }) => ($isDeck ? '#0891b2' : '#6b7280')};
  font-weight: 700;
  border-radius: 99px;
  padding: 2px 8px;
  flex-shrink: 0;
`;

const StyledExpandedContent = styled.div`
  padding-left: ${({ theme }) => theme.spacing.default};
  /* padding-top: ${({ theme }) => theme.spacing.small}; */
  /* margin-bottom: ${({ theme }) => theme.spacing.small}; */
`;

const SelectAllLink = styled.span`
  cursor: pointer;
  font-size: 11px;
  color: ${({ theme }) => theme.palette.textLinkColor};
  font-weight: 500;
  white-space: nowrap;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const StyledEmptyRow = styled.div`
  padding: 12px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.palette.grayLighter};
  font-size: ${({ theme }) => theme.fontSize.default};
`;

const GRID_MIN_ITEM_WIDTH = 120;
const GRID_MAX_COLS = 5;
const GRID_ITEM_GAP = 8;

export const StyledGrid = styled.div<{ $numCols: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $numCols }) => $numCols}, 1fr);
  align-items: flex-start;
  grid-gap: ${({ theme }) => theme.spacing.small};
  grid-row-gap: 3px;
  padding: 8px 0 12px;
`;
//endregion

const FOLDER_ROW_ACTIONS = [
  { name: FolderManagerItemActionNames.RENAME, type: FolderManagerItemActionTypes.SINGLE },
  { name: FolderManagerItemActionNames.MOVE, type: FolderManagerItemActionTypes.MULTI },
  { name: FolderManagerItemActionNames.ADD_SUBFOLDER, type: FolderManagerItemActionTypes.SINGLE },
  { name: FolderManagerItemActionNames.DELETE, type: FolderManagerItemActionTypes.MULTI },
];

export const FolderRow = ({
  item,
  depth,
  selectedRows,
  acceptedDragTypes,
  viewStateKey,
  selectedKeys,
  overId,
  enableDnD = false,
  disableUpload = false,
  parentFolderId,
  isParentSlideRepositoryFolder,
  disableSelectAll = false,
  compact = false,
  maxGridCols = GRID_MAX_COLS,
  addButtonTooltip,
  filters,
  addActionName = FolderManagerItemActionNames.ADD_TO_PRESENTATION,
  actionsConfig,
  disabledIds,
  isExpanded: isExpandedProp,
  onToggleExpand,
  onAction,
  onSelect,
  onDoubleClick,
}: FolderRowProps) => {
  const deferredFilters = filters || DEFAULT_SLIDES_FILTERS;
  const effectiveGroupByPptx = deferredFilters.groupBy === GROUP_BY_OPTIONS.PPTX;
  const [localIsExpanded, setLocalIsExpanded] = useState(
    () => item.type === FolderManagerMimeTypes.PPTX && effectiveGroupByPptx,
  );
  const isExpanded = isExpandedProp !== undefined ? isExpandedProp : localIsExpanded;
  const [previewState, setPreviewState] = useState<{ currentIndex: number } | null>(null);
  const [thumbnailPreviewItem, setThumbnailPreviewItem] = useState<{ id: number; name: string } | null>(null);

  const { data: thumbnailPreviewUrl } = useGetAssetThumbnailUrlQuery(
    thumbnailPreviewItem ? { assetId: thumbnailPreviewItem.id } : skipToken,
  );
  const [numCols, setNumCols] = useState(maxGridCols);
  const gridRef = useRef<HTMLDivElement>(null);

  const selectedThemeId = useSelector(viewStateSelectors.getSelectedThemeId(viewStateKey || ''));

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    let rafId: number;
    const observer = new ResizeObserver(([entry]) => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const cols = Math.min(
          maxGridCols,
          Math.max(1, Math.floor((entry.contentRect.width + GRID_ITEM_GAP) / (GRID_MIN_ITEM_WIDTH + GRID_ITEM_GAP))),
        );
        setNumCols((prev) => (prev !== cols ? cols : prev));
      });
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);

  const dndHooks = useDnDItem({
    item,
    overId: overId ?? null,
    selectedRows,
    thumbnailUrl: undefined,
    sortable: false,
    droppable: enableDnD,
  });
  const { isOver, listeners, attributes, setNodeRef } = dndHooks || {
    isOver: false,
    listeners: {},
    attributes: {},
    setNodeRef: null,
  };
  const dndAttributes = enableDnD ? attributes : {};
  const dndListeners = enableDnD ? listeners : {};

  const { openUploadNewAsset, openUploadNewAssetVersion } = useUploadNewAsset();

  const itemIsFolder = isFolder(item);
  const itemIsDeck = isDeck(item, isParentSlideRepositoryFolder);
  const displayName = item.displayName || item.name;

  const { data: rawSubFolders, isLoading: subFoldersLoading } = useGetSubFoldersQuery(
    { parentId: item.id as number },
    { skip: !itemIsFolder },
  );
  const { data: rawAssets, isLoading: assetsLoading } = useGetFolderAssetsQuery(
    { parentId: item.id as number },
    { skip: !itemIsFolder },
  );

  const isLoadingContents = itemIsFolder && (subFoldersLoading || assetsLoading);

  const contents = useMemo<FolderManagerItem[]>(() => {
    if (!itemIsFolder) return (item.slides || []).map((slide) => ({ ...slide, parent: item }));

    return [...(rawSubFolders || []), ...(rawAssets || [])]
      .map((child) => {
        const childIsFolder = isFolder(child);
        const isAccepted = childIsFolder || !acceptedDragTypes || acceptedDragTypes.includes(child.type);
        if (!isAccepted) return null;

        const filteredActions = (child.actions || []).filter(
          (a: any) => a.name !== FolderManagerItemActionNames.MOVE && a.name !== FolderManagerItemActionNames.COPY,
        );

        return {
          ...child,
          actions: childIsFolder
            ? filteredActions
            : [...filteredActions, { name: addActionName, type: FolderManagerItemActionTypes.MULTI }],
        };
      })
      .filter(Boolean) as FolderManagerItem[];
  }, [itemIsFolder, rawSubFolders, rawAssets, acceptedDragTypes, addActionName, item.slides]);

  const subFolders = useMemo(() => contents.filter(isFolder), [contents]);
  // all decks, unfiltered by date - gridItems applies the date filter per-slide instead, since
  // a deck's own updatedAt shouldn't gate the visibility of its individual slides
  const decks = useMemo(
    () => contents.filter((child) => isDeck(child, item.isSlideRepositoryFolder)),
    [contents, item.isSlideRepositoryFolder],
  );
  const pptxItems = useMemo(
    () => filterAndSortDecks(decks, deferredFilters),
    [decks, deferredFilters],
  );
  const thumbnailItems = useMemo(
    () => contents.filter((child) => isAsset(child, item.isSlideRepositoryFolder)),
    [contents, item.isSlideRepositoryFolder],
  );

  const sortedItems = useMemo(() => {
    const itemsWithDates = itemIsDeck
      ? thumbnailItems.map((slide: any) => ({
          ...slide,
          updatedAt: slide.itemData?.UpdatedAt ?? item.updatedAt,
          createdAt: slide.itemData?.CreatedAt ?? item.createdAt,
        }))
      : effectiveGroupByPptx
      ? thumbnailItems
      : [
          ...thumbnailItems,
          ...decks.flatMap((deck) =>
            (deck.slides || []).map((slide: any) => ({
              ...slide,
              parent: deck,
              updatedAt: slide.itemData?.UpdatedAt ?? deck.updatedAt,
              createdAt: slide.itemData?.CreatedAt ?? deck.createdAt,
            })),
          ),
        ];

    return filterAndSortSlides(itemsWithDates, deferredFilters);
  }, [itemIsDeck, effectiveGroupByPptx, thumbnailItems, decks, item, deferredFilters]);

  const gridItems = useMemo(
    () => markAlreadyAddedSlides(sortedItems, selectedKeys),
    [sortedItems, selectedKeys],
  );
  const previewSlides = gridItems;

  const totalSlides = useMemo(
    () =>
      itemIsFolder ? pptxItems.reduce((sum, child) => sum + (child.slides?.length ?? 0), 0) : item.slides?.length ?? 0,
    [itemIsFolder, pptxItems, item.slides],
  );
  const countToShow = totalSlides > 0 ? totalSlides : null;

  const handleAction = useCallback(
    (data: any) => {
      if (data?.actionType === FolderManagerActionTypes.SLIDE_PREVIEW) {
        const clickedItem = data.payload.item;
        if (clickedItem.type !== FolderManagerMimeTypes.SLIDE) {
          setThumbnailPreviewItem({ id: clickedItem.id, name: clickedItem.displayName || clickedItem.name });
          return;
        }
        const clickedIndex = previewSlides.findIndex(
          (s) => (clickedItem.instanceId && s.instanceId === clickedItem.instanceId) || s.id === clickedItem.id,
        );
        setPreviewState({ currentIndex: clickedIndex >= 0 ? clickedIndex : 0 });
        return;
      }
      onAction(data);
    },
    [onAction, previewSlides],
  );

  const handleNavigatePreview = useCallback((index: number) => setPreviewState({ currentIndex: index }), []);

  const handleItemSelect = useCallback(
    (data: any) => onSelect({ ...data, visibleItems: gridItems }),
    [onSelect, gridItems],
  );

  const handleToggle = useCallback(() => {
    if (onToggleExpand) {
      onToggleExpand();
    } else {
      setLocalIsExpanded((prev) => !prev);
    }
  }, []);

  const handleSelectAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const allSlides = itemIsDeck ? item.slides || [] : pptxItems.flatMap((child) => child.slides || []);
      onAction({ actionType: FolderManagerItemActionNames.ADD_TO_PRESENTATION, payload: { selectedItems: allSlides } });
    },
    [itemIsDeck, item.slides, pptxItems, onAction],
  );

  const sharedRowProps = useMemo(
    () => ({
      selectedRows,
      acceptedDragTypes,
      viewStateKey,
      selectedKeys,
      overId,
      enableDnD,
      disableUpload,
      parentFolderId: item.id as number,
      disableSelectAll,
      compact,
      maxGridCols,
      addButtonTooltip,
      filters,
      addActionName,
      actionsConfig,
      disabledIds,
      onAction: handleAction,
      onSelect,
      onDoubleClick,
    }),
    [
      selectedRows,
      acceptedDragTypes,
      viewStateKey,
      selectedKeys,
      overId,
      enableDnD,
      disableUpload,
      item.id,
      disableSelectAll,
      compact,
      maxGridCols,
      addButtonTooltip,
      filters,
      addActionName,
      actionsConfig,
      disabledIds,
      handleAction,
      onSelect,
      onDoubleClick,
    ],
  );

  return (
    <>
      <FileDropContainer
        isDisabled={disableUpload || (!itemIsFolder && !itemIsDeck)}
        isItemContainer
        acceptedMimeTypes={itemIsFolder ? item.allowed_mime_types : [item.type]}
        onUpload={(files) =>
          itemIsFolder
            ? openUploadNewAsset(files[0], item.id as number)
            : openUploadNewAssetVersion(
                files[0],
                item.id as number,
                displayName,
                parentFolderId as number,
                item.slides || [],
              )
        }
      >
        <StyledFolderRowWrapper ref={enableDnD ? setNodeRef : undefined} $compact={compact}>
          <StyledRowHeader
            $isDeck={itemIsDeck}
            $isOver={enableDnD && isOver}
            onClick={handleToggle}
            {...dndAttributes}
            {...dndListeners}
          >
            <span className="chevron">{isExpanded ? '▾' : '▸'}</span>

            <span className="row-icon">{itemIsFolder ? <FolderIcon /> : <DeckIcon />}</span>

            <span className="row-name">{displayName}</span>

            <span className="row-actions">
              {isLoadingContents ? (
                <CircularLoader size={16} />
              ) : (
                <>
                  {totalSlides > 0 && !disableSelectAll && (
                    <SelectAllLink onClick={handleSelectAll}>Select all</SelectAllLink>
                  )}
                  {countToShow != null && <StyledCountBadge $isDeck={itemIsDeck}>{countToShow}</StyledCountBadge>}
                  {(itemIsFolder || itemIsDeck) && (
                    <FolderManagerItemActions
                      item={item}
                      actions={
                        actionsConfig?.[item.type as FolderManagerMimeTypes] ??
                        (itemIsFolder ? FOLDER_ROW_ACTIONS : undefined)
                      }
                      onAction={onAction}
                    />
                  )}
                </>
              )}
            </span>
          </StyledRowHeader>

          <Activity mode={isExpanded ? 'visible' : 'hidden'}>
            <StyledExpandedContent>
              {isLoadingContents && contents.length === 0 && (
                <StyledEmptyRow>
                  <CircularLoader />
                </StyledEmptyRow>
              )}

              {!isLoadingContents && contents.length === 0 && (
                <StyledEmptyRow>{itemIsFolder ? 'No items in this folder.' : 'No slides in this deck.'}</StyledEmptyRow>
              )}

              <Activity mode={contents.length > 0 ? 'visible' : 'hidden'}>
                <>
                  {subFolders.map((child) => (
                    <FolderRow
                      key={`folder-${child.id}`}
                      item={child}
                      depth={depth + 1}
                      isParentSlideRepositoryFolder={item.isSlideRepositoryFolder}
                      {...sharedRowProps}
                    />
                  ))}

                  {effectiveGroupByPptx
                    ? pptxItems.map((child) => (
                        <FolderRow
                          key={`pptx-${child.id}`}
                          item={child}
                          depth={depth + 1}
                          isParentSlideRepositoryFolder={item.isSlideRepositoryFolder}
                          {...sharedRowProps}
                        />
                      ))
                    : null}

                  <Activity mode={gridItems.length > 0 ? 'visible' : 'hidden'}>
                    <SortableContext items={gridItems.map((gridItem) => String(gridItem.instanceId ?? gridItem.id))}>
                      <StyledGrid ref={gridRef} $numCols={numCols}>
                        {gridItems.map((child) => {
                          const configActions = actionsConfig?.[child.type as FolderManagerMimeTypes];
                          const slideItem =
                            configActions !== undefined
                              ? { ...child, actions: configActions }
                              : disableSelectAll
                              ? {
                                  ...child,
                                  actions: (child.actions || []).filter(
                                    (a: any) => a.name === FolderManagerItemActionNames.SLIDE_DOWNLOAD,
                                  ),
                                }
                              : child;
                          const isAlreadyAdded = child.selectable === false;
                          return (
                            <FolderManagerGridItem
                              key={`${child.type}-${child.instanceId ?? child.id}`}
                              item={slideItem}
                              selectedRows={selectedRows}
                              acceptedDragTypes={acceptedDragTypes}
                              viewStateKey={viewStateKey}
                              compact
                              isUploadDisabled
                              addButtonTooltip={addButtonTooltip}
                              disableDnD={(disableSelectAll && !enableDnD) || isAlreadyAdded}
                              disabled={disabledIds?.has(child.id)}
                              alreadyAdded={isAlreadyAdded}
                              onAction={handleAction}
                              onSelect={handleItemSelect}
                              onDoubleClick={() => onDoubleClick({ row: child })}
                            />
                          );
                        })}
                      </StyledGrid>
                    </SortableContext>
                  </Activity>
                </>
              </Activity>
            </StyledExpandedContent>
          </Activity>
        </StyledFolderRowWrapper>
      </FileDropContainer>

      {previewState && (
        <SlidePreviewDialog
          folderSlides={previewSlides}
          deckName={displayName}
          currentIndex={previewState.currentIndex}
          selectedKeys={selectedKeys}
          selectedThemeId={selectedThemeId}
          hideAddButton={disableSelectAll}
          onClose={() => setPreviewState(null)}
          onNavigate={handleNavigatePreview}
          onToggle={(_, checked) => {
            if (!previewState) return;
            const currentItem = previewSlides[previewState.currentIndex];
            if (!currentItem) return;
            const actionType = checked
              ? FolderManagerItemActionNames.ADD_TO_PRESENTATION
              : FolderManagerItemActionNames.REMOVE_FROM_PRESENTATION;
            onAction({ actionType, payload: { selectedItems: [currentItem] } });
          }}
        />
      )}

      <ImagePreviewDialog
        open={!!thumbnailPreviewItem && !!thumbnailPreviewUrl}
        imageUrl={thumbnailPreviewUrl ?? ''}
        alt={thumbnailPreviewItem?.name ?? ''}
        onClose={() => setThumbnailPreviewItem(null)}
      />
    </>
  );
};
